<template>
    <div v-if="viewsScope" class="table-views-bar mb-3">
        <div class="table-views-bar__row d-flex align-center">
            <div
                class="table-views-bar__views flex-grow-1 d-flex flex-wrap align-center"
            >
                <v-chip
                    v-for="(view, index) in views"
                    :key="view.id"
                    class="mr-2 my-1"
                    :color="activeViewId === view.id ? 'primary' : undefined"
                    :outlined="activeViewId !== view.id"
                    close
                    @click="applyView(view)"
                    @click:close="onChipClose(view)"
                >
                    {{ viewLabel(view, index) }}
                </v-chip>
                <span
                    v-if="loading"
                    class="text-caption grey--text my-1 ml-2"
                >
                    {{ $translate("loading") }}
                </span>
                <span
                    v-else-if="!views.length"
                    class="text-caption grey--text text--darken-1 my-1"
                >
                    {{ $translate("noViews") }}
                </span>
            </div>
            <div
                v-if="showFilterActions"
                class="table-views-bar__actions d-flex align-center flex-shrink-0"
            >
                <span
                    v-if="canReset"
                    class="link table-views-bar__reset mr-2"
                    role="button"
                    tabindex="0"
                    @click="resetFilters"
                    @keyup.enter="resetFilters"
                >
                    {{ $translate("resetFilters") }}
                </span>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="d-inline-flex">
                            <v-btn
                                icon
                                small
                                color="primary"
                                class="table-views-bar__save"
                                :loading="saving"
                                :disabled="saving || !canSave"
                                :aria-label="$translate('saveFilters')"
                                @click="onSaveClick"
                            >
                                <v-icon small>mdi-content-save</v-icon>
                            </v-btn>
                        </span>
                    </template>
                    <span>{{ $translate("saveFilters") }}</span>
                </v-tooltip>
            </div>
        </div>

        <v-dialog v-model="createDialog" max-width="420" persistent>
            <v-card class="table-views-save-dialog">
                <v-card-title class="table-views-save-dialog__title">
                    <div class="table-views-save-dialog__title-icon">
                        <v-icon color="primary" size="22">mdi-bookmark-plus-outline</v-icon>
                    </div>
                    <div>
                        <div class="table-views-save-dialog__heading">
                            {{ $translate("createDialogTitle") }}
                        </div>
                        <div class="table-views-save-dialog__subtitle">
                            {{ $translate("createDialogSubtitle") }}
                        </div>
                    </div>
                </v-card-title>
                <v-card-text class="table-views-save-dialog__body">
                    <v-text-field
                        v-model="createName"
                        :label="$translate('viewNameLabel')"
                        :error-messages="createNameErrors"
                        outlined
                        dense
                        autofocus
                        counter="128"
                        maxlength="128"
                        hide-details="auto"
                        @keyup.enter="confirmCreate"
                    />
                </v-card-text>
                <v-card-actions class="table-views-save-dialog__actions">
                    <v-spacer />
                    <v-btn text class="px-3" @click="closeCreateDialog">
                        {{ $translate("cancel") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        class="px-4"
                        depressed
                        :loading="saving"
                        :disabled="saving"
                        @click="confirmCreate"
                    >
                        {{ $translate("create") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import {
    extractApiFieldErrors,
    formatViewLabel,
    loadActiveViewId,
    saveActiveViewId,
    sanitizeFiltersForView,
} from "@/utils/tableViews";
import { migrateCatalogEntryFormatFilter } from "@/utils/apiFilterParams";

export default {
    mixins: [GlobalMixins],
    props: {
        viewsScope: {
            type: String,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        viewsExcludeKeys: {
            type: Array,
            default: () => [],
        },
        filtersConf: {
            type: Array,
            default: () => [],
        },
        showFilterActions: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            views: [],
            loading: false,
            saving: false,
            activeViewId: null,
            createDialog: false,
            createName: "",
            createNameErrors: [],
        };
    },
    computed: {
        sanitizedCurrentFilters() {
            return sanitizeFiltersForView(this.filters, this.viewsExcludeKeys);
        },
        canSave() {
            return Object.keys(this.sanitizedCurrentFilters).length > 0;
        },
        canReset() {
            return Object.keys(this.sanitizedCurrentFilters).length > 0;
        },
        activeView() {
            if (!this.activeViewId) {
                return null;
            }
            return this.views.find((view) => view.id === this.activeViewId) || null;
        },
    },
    watch: {
        viewsScope: {
            immediate: true,
            handler() {
                this.fetchViews();
            },
        },
        activeViewId(id) {
            saveActiveViewId(this.viewsScope, id);
            const view = this.views.find((item) => item.id === id);
            const name = view
                ? this.viewLabel(view, this.views.indexOf(view))
                : null;
            this.$emit("active-view-change", { id, name });
        },
    },
    methods: {
        clearActiveView() {
            this.activeViewId = null;
        },
        restoreActiveViewFromStorage() {
            const savedId = loadActiveViewId(this.viewsScope);
            if (savedId === null || savedId === undefined) {
                return;
            }
            const view = this.views.find(
                (item) => String(item.id) === String(savedId),
            );
            if (!view) {
                saveActiveViewId(this.viewsScope, null);
                return;
            }
            this.activeViewId = view.id;
        },
        viewLabel(view, index) {
            return formatViewLabel(view, index);
        },
        async fetchViews() {
            if (!this.viewsScope) {
                this.views = [];
                return;
            }
            this.loading = true;
            try {
                const response = await axios.get("/api/tableviews/", {
                    params: { scope: this.viewsScope },
                });
                this.views = response.data.results || response.data || [];
                this.restoreActiveViewFromStorage();
            } catch (error) {
                this.views = [];
                console.warn("Failed to load table views", error);
            } finally {
                this.loading = false;
            }
        },
        onSaveClick() {
            if (!this.canSave) {
                return;
            }
            if (this.activeView) {
                this.saveActiveViewFilters(this.activeView);
                return;
            }
            this.openCreateDialog();
        },
        openCreateDialog() {
            if (!this.canSave) {
                return;
            }
            this.createName = "";
            this.createNameErrors = [];
            this.createDialog = true;
        },
        closeCreateDialog() {
            this.createDialog = false;
            this.createName = "";
            this.createNameErrors = [];
        },
        async confirmCreate() {
            const filters = this.sanitizedCurrentFilters;
            if (!Object.keys(filters).length) {
                return;
            }
            const name = (this.createName || "").trim();
            this.createNameErrors = [];
            if (!name) {
                this.createNameErrors = [this.$translate("viewNameRequired")];
                return;
            }
            await this.createView(name, filters);
        },
        async saveActiveViewFilters(view) {
            const filters = this.sanitizedCurrentFilters;
            if (!Object.keys(filters).length) {
                return;
            }
            this.saving = true;
            try {
                const response = await axios.patch(`/api/tableviews/${view.id}/`, {
                    filters,
                });
                const index = this.views.findIndex((item) => item.id === view.id);
                if (index >= 0) {
                    this.$set(this.views, index, response.data);
                }
                this.activeViewId = response.data.id;
                this.addAlert(this.$translate("viewUpdated"));
            } catch (xhr_error) {
                this.handleSaveError(xhr_error);
            } finally {
                this.saving = false;
            }
        },
        async createView(name, filters) {
            this.saving = true;
            try {
                const response = await axios.post("/api/tableviews/", {
                    scope: this.viewsScope,
                    name,
                    filters,
                });
                this.views = [response.data, ...this.views];
                this.activeViewId = response.data.id;
                this.addAlert(this.$translate("viewSaved"));
                this.closeCreateDialog();
            } catch (xhr_error) {
                this.handleSaveError(xhr_error);
            } finally {
                this.saving = false;
            }
        },
        handleSaveError(xhr_error) {
            const error = this.$storeutils.handleError(xhr_error);
            const nameErrors = extractApiFieldErrors(error.details, "name");
            if (nameErrors?.length) {
                this.createNameErrors = nameErrors;
                return;
            }
            const filterErrors = extractApiFieldErrors(error.details, "filters");
            if (filterErrors?.length) {
                this.$swal({
                    title: this.$translate("saveFailed"),
                    html: filterErrors.join("<br/>"),
                    type: "error",
                });
                return;
            }
            this.$swal({
                title: this.$translate("saveFailed"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
        applyView(view) {
            if (this.activeViewId === view.id) {
                this.resetFilters();
                return;
            }
            this.activeViewId = view.id;
            this.$emit("apply", migrateCatalogEntryFormatFilter({ ...view.filters }));
        },
        onChipClose(view) {
            this.confirmDelete(view);
        },
        resetFilters() {
            this.activeViewId = null;
            this.$emit("reset");
        },
        async confirmDelete(view) {
            const result = await this.$swal({
                title: this.$translate("deleteConfirmTitle"),
                text: this.$translate("deleteConfirmText", {
                    name: view.name || this.viewLabel(view, 0),
                }),
                type: "warning",
                showCancelButton: true,
                confirmButtonText: this.$translate("delete"),
                cancelButtonText: this.$translate("cancel"),
            });
            if (result.dismiss) {
                return;
            }
            try {
                await axios.delete(`/api/tableviews/${view.id}/`);
                this.views = this.views.filter((v) => v.id !== view.id);
                if (this.activeViewId === view.id) {
                    this.activeViewId = null;
                }
                this.addAlert(this.$translate("viewDeleted"));
            } catch (xhr_error) {
                this.$swal({
                    title: this.$translate("deleteFailed"),
                    html: this.$utils.resultToSwalContent(
                        this,
                        this.$storeutils.handleError(xhr_error)
                    ),
                    type: "error",
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                saveFilters: "Save filters",
                resetFilters: "Reset filters",
                loading: "Loading…",
                noViews: "No saved views yet",
                createDialogTitle: "Create a view",
                createDialogSubtitle: "Save the current filters under a new name.",
                viewNameLabel: "View name",
                viewNameRequired: "Enter a name for this view",
                create: "Create",
                cancel: "Cancel",
                viewSaved: "Filter view saved",
                viewUpdated: "Filter view updated",
                saveFailed: "Could not save view",
                deleteConfirmTitle: "Delete this view?",
                deleteConfirmText:
                    'Delete "{name}"? This will remove the saved filters for everyone in your organization.',
                delete: "Delete",
                viewDeleted: "Filter view deleted",
                deleteFailed: "Could not delete view",
            },
            fr: {
                saveFilters: "Enregistrer les filtres",
                resetFilters: "Réinitialiser les filtres",
                loading: "Chargement…",
                noViews: "Aucune vue enregistrée",
                createDialogTitle: "Créer une vue",
                createDialogSubtitle:
                    "Enregistrez les filtres actuels sous un nouveau nom.",
                viewNameLabel: "Nom de la vue",
                viewNameRequired: "Indiquez un nom pour cette vue",
                create: "Créer",
                cancel: "Annuler",
                viewSaved: "Vue de filtres enregistrée",
                viewUpdated: "Vue de filtres mise à jour",
                saveFailed: "Impossible d'enregistrer la vue",
                deleteConfirmTitle: "Supprimer cette vue ?",
                deleteConfirmText:
                    'Supprimer « {name} » ? Les filtres enregistrés seront retirés pour toute l\'organisation.',
                delete: "Supprimer",
                viewDeleted: "Vue de filtres supprimée",
                deleteFailed: "Impossible de supprimer la vue",
            },
        },
    },
};
</script>

<style scoped>
.table-views-bar__row {
    min-height: 36px;
}

.table-views-bar__reset {
    white-space: nowrap;
    cursor: pointer;
}
</style>

<style lang="scss">
@import "@/assets/css/variables.scss";

.table-views-save-dialog {
    border-radius: 12px !important;
    overflow: hidden;
}

.table-views-save-dialog__title {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 24px 8px !important;
}

.table-views-save-dialog__title-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: $color-primary-extralight;
}

.table-views-save-dialog__heading {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.87);
}

.table-views-save-dialog__subtitle {
    margin-top: 2px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.77);
}

.table-views-save-dialog__body {
    padding: 8px 24px 8px !important;
}

.table-views-save-dialog__actions {
    padding: 8px 16px 16px !important;
    gap: 4px;
}
</style>
